import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, Shield, CheckCircle, XCircle, Clock, AlertTriangle, Loader, FileCheck, Eye, Download, Zap, Award, Building, Mail, Phone } from 'lucide-react';
import { verifierAPI } from '../api/api';

const VerifierDashboard: React.FC = () => {
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [verificationStatus, setVerificationStatus] = useState<any>(null);
  const [basicDetails, setBasicDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rollNumber, setRollNumber] = useState('');
  const [verifierId, setVerifierId] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [requestedFields, setRequestedFields] = useState<string[]>(['full_name', 'roll_number', 'current_year_sem', 'cgpa']);
  const [activeTab, setActiveTab] = useState('basic');

  const availableFields = [
    { key: 'full_name', label: '👤 Student Name', required: true },
    { key: 'roll_number', label: '🆔 Roll Number', required: true },
    { key: 'current_year_sem', label: '📅 Current Year & Semester', required: false },
    { key: 'passed_out', label: '🎓 Passed Out Year', required: false },
    { key: 'cgpa', label: '📊 CGPA', required: false },
    { key: 'backlogs', label: '⚠️ Backlogs', required: false },
    { key: 'gmail', label: '📧 Gmail Address', required: false },
    { key: 'phone_number', label: '📱 Phone Number', required: false },
    { key: 'remarks', label: '📝 Remarks', required: false }
  ];

  // Get basic details without verification
  const getBasicDetails = async () => {
    if (!rollNumber.trim()) {
      setError('Please provide a Roll Number');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response: any = await verifierAPI.getBasicDetails(rollNumber.toLowerCase());
      setBasicDetails(response.basic_details);
      setSuccess('✅ Basic details retrieved successfully!');
      
    } catch (err: any) {
      setError(err.message);
      setBasicDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // Check verification status
  const checkVerificationStatus = useCallback(async () => {
    if (!rollNumber.trim() || !verifierId.trim()) return;
    
    try {
      setLoading(true);
      setError('');
      
      const data: any = await verifierAPI.status(rollNumber.toLowerCase(), { verifier_id: verifierId });
      setVerificationStatus(data);
      
    } catch (err: any) {
      setError(err.message);
      setVerificationStatus(null);
    } finally {
      setLoading(false);
    }
  }, [rollNumber, verifierId]);

  // Request verification - Enhanced with company validation
  const requestVerification = async () => {
    if (!rollNumber.trim() || !verifierId.trim()) {
      setError('Please provide both Roll Number and Verifier ID');
      return;
    }

    if (!companyEmail.trim() || !companyPhone.trim() || !companyName.trim()) {
      setError('Please provide company email, phone, and name for verification');
      return;
    }

    if (requestedFields.length === 0) {
      setError('Please select at least one field to verify');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const response: any = await verifierAPI.request(rollNumber.toLowerCase(), {
        verifier_id: verifierId,
        company_name: companyName,
        company_email: companyEmail,
        company_phone: companyPhone,
        requested_fields: requestedFields
      });
      
      setSuccess(response.message || '⏳ Verification request submitted successfully!');
      await checkVerificationStatus();
      
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Verify record (after approval)
  const verifyRecord = async () => {
    if (!rollNumber.trim() || !verifierId.trim()) {
      setError('Please provide both Roll Number and Verifier ID');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const response: any = await verifierAPI.verify(rollNumber.toLowerCase(), {
        verifier_id: verifierId
      });
      
      setVerificationResult(response);
      setSuccess('✅ Record verified successfully!');
      
    } catch (err: any) {
      setError(err.message);
      setVerificationResult(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadVerificationReport = () => {
    if (!verificationResult) return;
    
    const report = {
      verification_report: {
        roll_number: verificationResult.roll_number,
        verified_at: verificationResult.verified_at,
        verifier_id: verifierId,
        company_name: companyName,
        company_email: companyEmail,
        company_phone: companyPhone,
        status: 'VERIFIED',
        verification_data: verificationResult.verification_data
      }
    };
    
    const dataStr = JSON.stringify(report, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `verification_report_${verificationResult.roll_number}_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      approved: <CheckCircle className="h-5 w-5 text-neon-green" />,
      pending: <Clock className="h-5 w-5 text-yellow-400" />,
      not_requested: <AlertTriangle className="h-5 w-5 text-gray-400" />,
      not_found: <XCircle className="h-5 w-5 text-red-400" />,
      deleted: <XCircle className="h-5 w-5 text-red-400" />
    };
    return icons[status as keyof typeof icons] || <AlertTriangle className="h-5 w-5 text-gray-400" />;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      approved: 'text-neon-green border-neon-green/50 bg-neon-green/10',
      pending: 'text-yellow-400 border-yellow-400/50 bg-yellow-400/10',
      not_requested: 'text-gray-400 border-gray-400/50 bg-gray-400/10',
      not_found: 'text-red-400 border-red-400/50 bg-red-400/10',
      deleted: 'text-red-400 border-red-400/50 bg-red-400/10'
    };
    return colors[status as keyof typeof colors] || colors.not_requested;
  };

  const handleFieldToggle = (field: string) => {
    if (availableFields.find(f => f.key === field)?.required) return;
    
    setRequestedFields(prev => 
      prev.includes(field) 
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  return (
    <motion.div 
      className="min-h-screen p-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <h1 className="text-7xl font-bold bg-gradient-to-r from-neon-purple to-purple-400 bg-clip-text text-transparent mb-6">
            🔍 Verifier Portal
          </h1>
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto">
            Authenticate academic credentials with blockchain verification and cryptographic proof
          </p>
        </motion.div>

        {/* Success/Error Messages */}
        {error && (
          <motion.div 
            className="alert-error p-4 rounded-lg flex items-center space-x-3 mb-6"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <XCircle className="h-5 w-5 text-red-400" />
            <span>{error}</span>
          </motion.div>
        )}

        {success && (
          <motion.div 
            className="alert-success p-4 rounded-lg flex items-center space-x-3 mb-6"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span>{success}</span>
          </motion.div>
        )}

        {/* Navigation Tabs */}
        <motion.div 
          className="flex justify-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="glass-card p-2 rounded-xl">
            <div className="flex space-x-2">
              {[
                { id: 'basic', label: '👁️ Basic Details', icon: Eye },
                { id: 'verify', label: '🔍 Request Verification', icon: Shield },
                { id: 'status', label: '📋 Check Status', icon: FileCheck }
              ].map((tab) => (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-neon-purple to-purple-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Verifier Credentials */}
            <motion.div 
              className="glass-card p-6 rounded-xl"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h2 className="text-2xl font-semibold text-white mb-4 flex items-center">
                <Shield className="h-6 w-6 mr-2 text-neon-purple" />
                Verifier Credentials
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Roll Number to Verify</label>
                  <input
                    type="text"
                    value={rollNumber}
                    onChange={(e) => setRollNumber(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-purple/30 focus:border-neon-purple focus:outline-none font-mono tracking-wider"
                    placeholder="e.g., 229x1a2856"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Your Verifier ID</label>
                  <input
                    type="text"
                    value={verifierId}
                    onChange={(e) => setVerifierId(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-purple/30 focus:border-neon-purple focus:outline-none"
                    placeholder="e.g., company_hr_dept"
                  />
                </div>
              </div>

              {/* Company Validation Fields */}
              {(activeTab === 'verify' || activeTab === 'status') && (
                <div className="mt-6 p-4 bg-neon-blue/10 border border-neon-blue/30 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Building className="h-5 w-5 mr-2 text-neon-blue" />
                    Company Verification Details
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Company Name</label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                        placeholder="e.g., TechCorp Solutions"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Company Email</label>
                      <input
                        type="email"
                        value={companyEmail}
                        onChange={(e) => setCompanyEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                        placeholder="hr@company.com"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Company Phone</label>
                      <input
                        type="tel"
                        value={companyPhone}
                        onChange={(e) => setCompanyPhone(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg text-white bg-dark-card/80 border border-neon-blue/30 focus:border-neon-blue focus:outline-none"
                        placeholder="+91 XXXXXXXXXX"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3 p-2 bg-yellow-400/10 border border-yellow-400/30 rounded">
                    <p className="text-xs text-yellow-400">
                      🔒 Company details are required for verification requests and will be shared with the institution for validation
                    </p>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Basic Details Tab */}
            {activeTab === 'basic' && (
              <motion.div 
                className="space-y-6"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="glass-card p-6 rounded-xl">
                  <h3 className="text-xl font-semibold text-white mb-4">Get Basic Student Details</h3>
                  <p className="text-gray-300 text-sm mb-4">
                    Access basic information (Roll Number, Name, CGPA, Backlogs) without verification approval.
                  </p>
                  
                  <motion.button
                    onClick={getBasicDetails}
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-neon-green to-green-600 hover:from-green-600 hover:to-neon-green rounded-lg font-semibold text-white disabled:opacity-50 transition-all duration-300 flex items-center justify-center space-x-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? (
                      <Loader className="animate-spin h-5 w-5" />
                    ) : (
                      <>
                        <Eye className="h-5 w-5" />
                        <span>Get Basic Details</span>
                      </>
                    )}
                  </motion.button>
                </div>

                {/* Basic Details Display */}
                {basicDetails && (
                  <motion.div 
                    className="glass-card p-6 rounded-xl border border-neon-green/50"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      <Eye className="h-6 w-6 text-neon-green" />
                      <h3 className="text-xl font-semibold text-white">Basic Student Details</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 glass-card rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <Shield className="h-4 w-4 text-neon-blue" />
                          <p className="text-sm text-gray-400">Student Name</p>
                        </div>
                        <p className="text-white font-bold text-lg">{basicDetails.full_name}</p>
                      </div>
                      
                      <div className="p-4 glass-card rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <FileCheck className="h-4 w-4 text-neon-purple" />
                          <p className="text-sm text-gray-400">Roll Number</p>
                        </div>
                        <p className="text-neon-blue font-mono font-bold text-lg">{basicDetails.roll_number}</p>
                      </div>
                      
                      <div className="p-4 glass-card rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <Award className="h-4 w-4 text-neon-green" />
                          <p className="text-sm text-gray-400">CGPA</p>
                        </div>
                        <p className="text-neon-green font-bold text-2xl">{basicDetails.cgpa}</p>
                      </div>
                      
                      <div className="p-4 glass-card rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-400" />
                          <p className="text-sm text-gray-400">Backlogs</p>
                        </div>
                        <p className="text-yellow-400 font-bold text-2xl">{basicDetails.backlogs}</p>
                      </div>
                      
                      <div className="p-4 glass-card rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <Clock className="h-4 w-4 text-indigo-400" />
                          <p className="text-sm text-gray-400">Current Year & Sem</p>
                        </div>
                        <p className="text-white font-medium">{basicDetails.current_year_sem}</p>
                      </div>
                      
                      <div className="p-4 glass-card rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-400" />
                          <p className="text-sm text-gray-400">Status</p>
                        </div>
                        <p className={`font-bold ${basicDetails.status === 'active' ? 'text-neon-green' : 'text-red-400'}`}>
                          {basicDetails.status.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-neon-blue/10 border border-neon-blue/30 rounded-lg">
                      <p className="text-sm text-neon-blue">
                        💡 Need more details like contact information, grades, or remarks? Use the "Request Verification" tab to get additional information with institution approval.
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Verify Tab */}
            {activeTab === 'verify' && (
              <>
                {/* Field Selection */}
                <motion.div 
                  className="glass-card p-6 rounded-xl"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <h3 className="text-xl font-semibold text-white mb-4">Select Additional Fields to Verify</h3>
                  <p className="text-gray-300 text-sm mb-4">
                    Choose additional information you need beyond basic details. Institution approval required.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {availableFields.map((field) => (
                      <label
                        key={field.key}
                        className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          requestedFields.includes(field.key)
                            ? 'bg-neon-purple/10 border-neon-purple/50'
                            : 'bg-gray-700/20 border-gray-600/50 hover:border-gray-500/50'
                        } ${field.required ? 'opacity-75 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={requestedFields.includes(field.key)}
                          onChange={() => handleFieldToggle(field.key)}
                          disabled={field.required}
                          className="w-4 h-4 text-neon-purple bg-dark-card border-gray-600 rounded focus:ring-neon-purple focus:ring-2"
                        />
                        <span className="text-sm text-gray-300 flex-1">
                          {field.label}
                          {field.required && <span className="text-neon-purple ml-1">*</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-3 bg-neon-purple/10 border border-neon-purple/30 rounded-lg">
                    <p className="text-xs text-neon-purple">
                      * Required fields are automatically included. Additional fields require institution approval.
                    </p>
                  </div>
                </motion.div>

                {/* Action Buttons */}
                <motion.div 
                  className="glass-card p-6 rounded-xl"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <motion.button
                      onClick={requestVerification}
                      disabled={loading}
                      className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-orange-500 hover:to-yellow-500 rounded-lg font-semibold text-white disabled:opacity-50 transition-all duration-300 flex items-center justify-center space-x-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {loading ? (
                        <Loader className="animate-spin h-5 w-5" />
                      ) : (
                        <>
                          <Clock className="h-5 w-5" />
                          <span>Request Verification</span>
                        </>
                      )}
                    </motion.button>
                    
                    <motion.button
                      onClick={verifyRecord}
                      disabled={loading}
                      className="w-full py-3 bg-gradient-to-r from-neon-purple to-purple-600 hover:from-purple-600 hover:to-neon-purple rounded-lg font-semibold text-white disabled:opacity-50 transition-all duration-300 flex items-center justify-center space-x-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {loading ? (
                        <Loader className="animate-spin h-5 w-5" />
                      ) : (
                        <>
                          <Shield className="h-5 w-5" />
                          <span>Verify Now</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              </>
            )}

            {/* Status Tab */}
            {activeTab === 'status' && (
              <motion.div 
                className="glass-card p-6 rounded-xl"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h3 className="text-xl font-semibold text-white mb-4">Check Verification Status</h3>
                
                <motion.button
                  onClick={checkVerificationStatus}
                  disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-neon-blue to-blue-600 hover:from-blue-600 hover:to-neon-blue rounded-lg font-semibold text-white disabled:opacity-50 transition-all duration-300 flex items-center justify-center space-x-2 mb-6"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <Loader className="animate-spin h-5 w-5" />
                  ) : (
                    <>
                      <Search className="h-5 w-5" />
                      <span>Check Status</span>
                    </>
                  )}
                </motion.button>

                {verificationStatus && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 glass-card rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(verificationStatus.verification_status)}
                        <div>
                          <p className="text-white font-medium">Verification Status</p>
                          <p className="text-sm text-gray-400">
                            {verificationStatus.student_name ? `for ${verificationStatus.student_name}` : 'Status check'}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getStatusColor(verificationStatus.verification_status)}`}>
                        {verificationStatus.verification_status.toUpperCase().replace('_', ' ')}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 glass-card rounded-lg text-center">
                        <p className="text-sm text-gray-400">Has Approved Verification</p>
                        <p className={`text-lg font-bold ${verificationStatus.has_approved_verification ? 'text-neon-green' : 'text-red-400'}`}>
                          {verificationStatus.has_approved_verification ? 'YES' : 'NO'}
                        </p>
                      </div>
                      <div className="p-3 glass-card rounded-lg text-center">
                        <p className="text-sm text-gray-400">Pending Requests</p>
                        <p className={`text-lg font-bold ${verificationStatus.has_pending_requests ? 'text-yellow-400' : 'text-gray-400'}`}>
                          {verificationStatus.has_pending_requests ? 'YES' : 'NO'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Guide */}
            <motion.div 
              className="glass-card p-6 rounded-xl"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Zap className="h-5 w-5 mr-2 text-yellow-400" />
                Quick Guide
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3 text-sm">
                  <span className="bg-neon-green text-black rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs">1</span>
                  <div>
                    <p className="text-white font-medium">Get Basic Details</p>
                    <p className="text-gray-400 text-xs">Access roll number, name, CGPA, backlogs instantly</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 text-sm">
                  <span className="bg-yellow-400 text-black rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs">2</span>
                  <div>
                    <p className="text-white font-medium">Request Additional Info</p>
                    <p className="text-gray-400 text-xs">Submit request with company details for approval</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 text-sm">
                  <span className="bg-neon-purple text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs">3</span>
                  <div>
                    <p className="text-white font-medium">Verify & Download</p>
                    <p className="text-gray-400 text-xs">Once approved, verify and download report</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Company Info Display */}
            {(companyName || companyEmail || companyPhone) && (
              <motion.div 
                className="glass-card p-6 rounded-xl"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <Building className="h-5 w-5 mr-2 text-neon-blue" />
                  Company Details
                </h3>
                
                <div className="space-y-3">
                  {companyName && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Company:</span>
                      <span className="text-white font-medium">{companyName}</span>
                    </div>
                  )}
                  
                  {companyEmail && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Email:</span>
                      <span className="text-neon-blue text-sm">{companyEmail}</span>
                    </div>
                  )}
                  
                  {companyPhone && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Phone:</span>
                      <span className="text-green-400 text-sm">{companyPhone}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Verification Result */}
            {verificationResult && (
              <motion.div 
                className="glass-card p-6 rounded-xl border border-neon-green/50"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <Award className="h-6 w-6 text-neon-green" />
                  <h3 className="text-xl font-semibold text-white">Verification Result</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Status:</span>
                    <span className="px-2 py-1 bg-neon-green/20 text-neon-green rounded-full text-xs font-bold">
                      VERIFIED ✓
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Roll Number:</span>
                    <span className="text-neon-blue font-mono">
                      {verificationResult.roll_number?.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Verified At:</span>
                    <span className="text-gray-300 text-sm">
                      {new Date(verificationResult.verified_at).toLocaleString()}
                    </span>
                  </div>
                  
                  {verificationResult.verification_data?.data && (
                    <div className="mt-4 p-4 bg-neon-green/10 border border-neon-green/30 rounded-lg">
                      <h4 className="text-sm font-medium text-neon-green mb-2">Verified Information:</h4>
                      <div className="space-y-1 text-xs">
                        {Object.entries(verificationResult.verification_data.data).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-400 capitalize">{key.replace('_', ' ')}:</span>
                            <span className="text-white font-medium">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <motion.button
                    onClick={downloadVerificationReport}
                    className="w-full mt-4 py-2 bg-gradient-to-r from-neon-green to-green-600 hover:from-green-600 hover:to-neon-green rounded-lg font-medium text-white transition-all duration-300 flex items-center justify-center space-x-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Report</span>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Statistics */}
            <motion.div 
              className="glass-card p-6 rounded-xl"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Verification Stats</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Basic Details Accessed:</span>
                  <span className="text-neon-blue font-bold">
                    {basicDetails ? '1' : '0'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Full Verifications:</span>
                  <span className="text-neon-green font-bold">
                    {verificationResult ? '1' : '0'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Success Rate:</span>
                  <span className="text-neon-green font-bold">
                    {(basicDetails || verificationResult) ? '100%' : '0%'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Last Activity:</span>
                  <span className="text-gray-400 text-sm">
                    {(basicDetails || verificationResult) ? 'Just now' : 'Never'}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VerifierDashboard;
